import React, { useEffect, useRef, useState } from 'react'
import { IconButton, makeStyles } from '@material-ui/core';
import { datesAreOnSameDay, prettyDate } from '../../config/dateFormatting';
import { MdArrowBackIos, MdArrowForwardIos } from 'react-icons/md';
import { Calendar, DateObject } from 'react-multi-date-picker';
import { fromDateObjectToDate, fromDateToDateObject, handleChangeDateObject } from '../../utils/calendarDateHelpers';
import { useOnClickOutside } from 'usehooks-ts';

const useStyles = makeStyles((theme: any) => ({
    weekSelectionContainer: {
        display: 'grid',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '80px',
        gridTemplateColumns: "48px 250px 48px",
    },
    weekTitleContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        width: '100%',
        marginLeft: 5,
        marginRight: 5,
        cursor: "pointer"
    },
    calendar: {
        border: "1px solid black"
    },
    calendarContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: "absolute",
        width: "100%",
        "& > *": {
            maxWidth: "300px !important",
            marginRight: 60
        }
    },
    todayLink: {
        color: theme.palette.primary.main,
        display: "flex",
        width: "100%",
        justifyContent: "center",
        marginTop: -10,
        marginBottom: 15,
        "&:hover": {
            textDecoration: "underline",
            cursor: "pointer"
        }
    }
}));

interface Props {
    date: Date,
    setDate: React.Dispatch<React.SetStateAction<Date>>;
    setWeekDates: React.Dispatch<React.SetStateAction<Date[]>>;
    updateWeekNumber?: (update: boolean) => void;
    canCalendarPick?: boolean;
}

export const WeekPicker: React.FC<Props> = ({date,setDate,setWeekDates,updateWeekNumber,canCalendarPick }) => {
    const classes = useStyles();
    const [weekNumber, setWeekNumber] = useState(0);
    const [weekNumberString, setWeekNumberString] = useState('');
    const [isCalendarPicking, setIsCalendarPicking] = useState(false)

    // Click outside calendar
    const ref = useRef(null)

    const handleClickOutside = () => {
        setIsCalendarPicking(false)
    }

    useOnClickOutside(ref, handleClickOutside)

    useEffect(() => {
        getWeekNumber(date)
    }, []);

    useEffect(() => {
        getWeekNumber(date)
    }, [date]);

    function getWeekNumber(date: Date) {
        // Copy date so don't modify original
        date = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        // Set to nearest Thursday: current date + 4 - current day number
        // Make Sunday's day number 7
        date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay()||7));
        // Get first day of year
        var yearStart = new Date(Date.UTC(date.getUTCFullYear(),0,1));
        // Calculate full weeks to nearest Thursday
        var tmpWeekNumber = Math.ceil(( ( (date.getTime() - yearStart.getTime()) / 86400000) + 1)/7)
        setWeekNumber(tmpWeekNumber);

        // Get week number string
        var startDayOfWeek = getDateOfISOWeek(tmpWeekNumber, date.getFullYear());
        var fridayOfWeek = new Date(startDayOfWeek);
        fridayOfWeek.setDate(fridayOfWeek.getDate() + 4)

        // Set short date
        const monthAbbreviations = ["Jan.", "Feb.", "Mar.", "Apr.", "May.", "Jun.", "Jul.", "Aug.", "Sep.", "Oct.", "Nov.", "Dec."];
        if(startDayOfWeek.getMonth() === fridayOfWeek.getMonth()){
            setWeekNumberString(startDayOfWeek.getDate() + ' - ' + fridayOfWeek.getDate() + ' ' + monthAbbreviations[startDayOfWeek.getMonth()] + ' ' + startDayOfWeek.getFullYear())
        } else {
            setWeekNumberString(prettyDate(startDayOfWeek, false, true) + ' - ' + prettyDate(fridayOfWeek, false, true))
        }


        // Create array of dates:
        let tmpWeekDates: Date[] = [];
        for (let i = 0; i < 5; i++) {
            tmpWeekDates.push(new Date(startDayOfWeek))
            tmpWeekDates[i].setDate(tmpWeekDates[i].getDate() + i)
        }
        setWeekDates(tmpWeekDates)
    }

    const updateWeekNumberWithoutWarning = (increase: boolean) => {
        if (increase) {
            date.setDate(date.getDate() + 7)
            setDate(new Date(date))
        } else {
            date.setDate(date.getDate() - 7)
            setDate(new Date(date))
        }
    }

    const getDateOfISOWeek = (week: number, year: number) => {
        var simple = new Date(year, 0, 1 + (week - 1) * 7);
        var dow = simple.getDay();
        var ISOweekStart = simple;
        if (dow <= 4)
            ISOweekStart.setDate(simple.getDate() - simple.getDay() + 1);
        else
            ISOweekStart.setDate(simple.getDate() + 8 - simple.getDay());
        return ISOweekStart;
    }

    useEffect(() => {
        setIsCalendarPicking(false)
    },[date])

    return (
        <div>
        {
            updateWeekNumber ?
                <div>
                    <div className={classes.weekSelectionContainer}>
                        <IconButton color="secondary" aria-label="add to shopping cart" onClick={() => { updateWeekNumber(false) }}>
                            <MdArrowBackIos />
                        </IconButton>
                        <div className={classes.weekTitleContainer} onClick={() => setIsCalendarPicking(!isCalendarPicking)}>
                            <div style={{ 'fontSize': '24px', 'fontWeight': 700, 'color': '#374251' }}>Week {weekNumber}</div>
                            <div style={{ 'fontSize': '12px', 'fontWeight': 400, 'color': '#374251' }}>{weekNumberString}</div>
                        </div>
                        <IconButton color='primary'  aria-label="add to shopping cart" onClick={() => { updateWeekNumber(true) }}>
                            <MdArrowForwardIos />
                        </IconButton>
                    </div>
                    {
                        canCalendarPick &&
                        <div className={classes.todayLink} onClick={() => {setDate(new Date())}}>
                            Today
                        </div>
                    }
                </div>
            :
            <div>
                <div className={classes.weekSelectionContainer}>
                    <IconButton color="secondary" aria-label="add to shopping cart" onClick={() => { updateWeekNumberWithoutWarning(false) }}>
                        <MdArrowBackIos />
                    </IconButton>
                    <div className={classes.weekTitleContainer} onClick={() => setIsCalendarPicking(!isCalendarPicking)}>
                        <div style={{ 'fontSize': '24px', 'fontWeight': 700, 'color': '#374251' }}>Week {weekNumber}</div>
                        <div style={{ 'fontSize': '12px', 'fontWeight': 400, 'color': '#374251' }}>{weekNumberString}</div>
                    </div>
                    <IconButton color='primary'  aria-label="add to shopping cart" onClick={() => { updateWeekNumberWithoutWarning(true) }}>
                        <MdArrowForwardIos />
                    </IconButton>
                </div>
                    {
                        canCalendarPick &&
                        <div className={classes.todayLink} onClick={() => {setDate(new Date())}}>
                            Today
                        </div>
                    }
            </div>
        }
        {
                canCalendarPick && isCalendarPicking &&
                <div className={classes.calendarContainer} ref={ref}>
                    <Calendar
                        currentDate={fromDateToDateObject(date)}
                        multiple={false}
                        value={date} 
                        onChange={(e) => handleChangeDateObject(e,setDate)}
                        weekStartDayIndex={1}
                        showOtherDays={false}
                        className={classes.calendar}
                        mapDays={({ date }) => {
                            // Styling of today
                            if(datesAreOnSameDay(date.toDate(), new DateObject().toDate())){
                                return {
                                    style: { background: "#6178ed7c", color:'white' },
                                    id: "today-calendar-date",
                                    className: "calendar-date",
                                }
                            }

                            // Styling of weekends
                            let isWeekend = [0, 6].includes(date.weekDay.index)
                            if (isWeekend) return {
                                disabled: true,
                                style: { color: "#ccc" },
                                className: "calendar-date",
                                onClick: () => alert("Weekends are disabled")
                            }

                            // Identifiers for date spans
                            return{
                                className: "calendar-date",
                            }
                        }}
                    />
                </div>
            }
        </div>
    );
}